<style scoped lang="sass">
#pool-page
  background-color: white !important
  .subscription-warning
    display: flex
    justify-content: space-between
    align-items: center
    background-color: red
    color: white
    padding: 0.5rem
    img
      height: 1.5rem
    .action
      font-size: 0.8rem
    >*
      margin-right: 0.5rem
      margin-top: 0
      &:last-child
        margin-right: 0
  .top-section
    background-color: var(--light-gray)
    padding: 0 var(--base-padding)
    .switcher
      display: flex
      justify-content: space-between
      padding: var(--base-padding) 0
      .button
        width: auto
        border-radius: 2rem
        border: 1px solid transparent
        background-color: white
        font-size: 0.75rem
        font-weight: 600
        flex: 1 1 0
        margin-right: var(--base-padding)
        &.active
          background-color: transparent
          border: 1px solid var(--medium-gray)
        &:last-child
          margin-right: 0
    .pool-info
      padding-bottom: 1rem
      .pool-name-line
        display: flex
        justify-content: space-between
        align-items: center
      .tournament-name
        font-size: 1.5rem
        font-weight: 300
      .tournament-time
        font-size: 0.75rem
        font-weight: 600
        .tournament-date-range
          color: var(--medium-gray)
          margin-right: 0.25rem
        .tournament-starts
          color: #409e47
        .tournament-auto-refresh
          margin-left: 0.5rem
          color: var(--medium-gray)
</style>
<template>
  <div class="page" id="pool-page" v-if="pool">
    <div class="subscription-warning" v-if="pool && showPaymentWarning">
      <img src="../assets/images/icon-stop.svg">
      <div>
        You have to pay to play.<br />
        Only $2 a month.
      </div>
      <a href="#" @click.prevent="$root.togglePaymentModal" class="button empty inline action">PAY NOW</a>
    </div>
    <div class="top-section">
      <div class="switcher">
        <button
            @click.prevent="setSection('pool-leaderboard')"
            class="button"
            :class="{active: this.section === 'pool-leaderboard'}"
            >POOL</button>
        <button
            @click.prevent="setSection('tournament-leaderboard')"
            class="button"
            :class="{active: this.section === 'tournament-leaderboard'}"
            >TOURNAMENT</button>
        <button
            @click.prevent="setSection('pool-details')"
            class="button"
            :class="{active: this.section === 'pool-details'}"
            >DETAILS</button>
      </div>
      <div class="pool-info">
        <div class="pool-name-line">
          <span>{{pool.name}}</span>
          <span>
            <router-link :to="{name: 'editPool', id: pool.id}">
              <img 
                src="../assets/images/icon-dots.svg"
                v-if="poolEditable"
                >
            </router-link>
          </span>
        </div>
        <div class="tournament-name">{{pool.tournament.name}}</div>
        <div class="tournament-time">
          <span class="tournament-date-range">{{tournamentDateRange(pool)}}</span>
          <span class="tournament-starts" v-if="!tournamentStarted">STARTS IN {{tournamentStartsIn}}</span>
          <span class="tournament-auto-refresh" v-else>AUTO-REFRESHED {{tournamentRefreshedAgo}}</span>
        </div>
      </div>
    </div>

    <pool-leaderboard v-if="section === 'pool-leaderboard'" :pool="pool"></pool-leaderboard>
    <tournament-leaderboard v-if="section === 'tournament-leaderboard'" :pool="pool"></tournament-leaderboard>
    <pool-details v-if="section === 'pool-details'" :pool="pool"></pool-details>
    <chat v-if="chat" :pool="pool" @close="toggleChat"></chat>
  </div>
</template>
<script>
import pluralize from 'pluralize';
import moment    from 'moment';
import _         from 'lodash';

import PoolLeaderboard from './pool-leaderboard.vue';
import TournamentLeaderboard from './tournament-leaderboard.vue';
import PoolDetails from './pool-details.vue';
import Chat from './chat.vue';

export default {
  name: 'Pool',
  components: {
    PoolLeaderboard,
    TournamentLeaderboard,
    PoolDetails,
    Chat
  },
  created() {
    this.setNavigationConfig();
    this.updateNow();
    this.updateCachedPool();
    this.nowInterval    = setInterval(() => this.updateNow(), 3000);
    this.updateInterval = setInterval(() => this.updateCachedPool(), 120000);
  },
  beforeDestroy() {
    clearInterval(this.nowInterval);
    clearInterval(this.updateInterval);
  },
  data: () => {
    return {
      section:        'pool-leaderboard',
      now:            null,
      nowInterval:    null,
      updateInterval: null,
      chat:           false
    }
  },
  computed: {
    pool() {
      return this.$store.getters.getCachedPool(parseInt(this.$route.params.id));
    },
    anyNewComments() {
      return _.get(this, 'pool.participation.any_new_comments');
    },
    tournamentStarted() {
      return this.pool ? moment().isAfter(this.pool.tournament.start_time) : false;
    },
    poolEditable() {
      return this.pool && !this.tournamentStarted && this.pool.owner.username === this.$root.currentUser.username;
    },
    cachedPoolUpdatedAt() {
      return this.$store.getters.getCachedPoolUpdatedAt(parseInt(this.$route.params.id));
    },
    tournamentStartsIn() {
      let ret = null;
      if (this.pool) {
        const start = moment(this.pool.tournament.start_time);
        const days = start.diff(moment(), 'days');
        const hours = start.subtract(days, 'days').diff(moment(), 'hours');
        if (days > 0) {
          if (hours > 0) {
            ret = `${pluralize('day', days, true)} ${pluralize('hour', hours, true)}`.toUpperCase();
          } else {
            ret = `${pluralize('day', days, true)}`.toUpperCase();
          }
        } else {
          ret = `${pluralize('hour', hours, true)}`.toUpperCase();
        }
      }
      return ret;
    },
    tournamentRefreshedAgo() {
      return this.cachedPoolUpdatedAt && this.now.isAfter(this.cachedPoolUpdatedAt) ? this.now.to(this.cachedPoolUpdatedAt).toUpperCase() : 'JUST NOW';
    },
    showPaymentWarning() {
      return (
        (this.pool && !this.pool.tournament.ended) &&
        (
          (!this.tournamentStarted && !this.$root.currentUserSolventThrough4Days) ||
          (this.tournamentStarted && !this.$root.currentUserSolventThrough4Days && this.pool.pool_leaderboard.length > 4)
        )
      );
    }
  },
  methods: {
    updateNow() {
      this.now = moment();
    },
    updateCachedPool() {
      return this.$store.dispatch('updateCachedPool', {params: {id: this.$route.params.id}});
    },
    tournamentDateRange(pool) {
      const startTime = moment(pool.tournament.start_time);
      const endTime = moment(startTime).add(3, 'days');
      const showYear = startTime.format('YYYY') === moment().format('YYYY');
      return `${startTime.format('MMM D')} - ${endTime.format('D')}${showYear ? '' : startTime.format(' YYYY')}`.toUpperCase();
    },
    setSection(section) {
      this.section = section;
    },
    toggleChat() {
      this.chat = !this.chat;
      if (!this.chat) {
        this.updateCachedPool()
          .then(() => this.setNavigationConfig());
      }
    },
    setNavigationConfig() {
      this.$store.dispatch('configNavigation', {
        leftEnabled: true,
        leftRoute: {name: 'dashboard'},
        leftImg: 'icon-back.svg',
        rightEnabled: true,
        rightHandler: this.toggleChat,
        rightImg: this.anyNewComments ? 'icon-chat-notification.png' : 'icon-chat.png'
      });
    }
  },
  watch: {
    anyNewComments() {
      if (!this.chat) {
        this.setNavigationConfig();
      }
    }
  }
};
</script>
